import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Contact, ContactService } from '../contacts/contact.service';
import { LifecycleStage, DealService } from './deal.service';
import { Lead, LeadService } from '../leads/lead.service';
import { Pipeline, PipelineService } from '../pipeline/pipeline.service';
import { AuthContext } from '../../../context/authcontext';

interface DealFormData {
  contacts: Contact[];
  lifecycleStages: LifecycleStage[];
  leads: Lead[];
  pipelines: Pipeline[];
  loading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  getFilteredContacts: (companyId: number | null) => Contact[];
}

const DealFormDataContext = createContext<DealFormData | undefined>(undefined);

interface DealFormDataProviderProps {
  children: ReactNode;
  isMainApiCompleted?: boolean;
  shouldLoad?: boolean;
}

export const DealFormDataProvider: React.FC<DealFormDataProviderProps> = ({ children, isMainApiCompleted = false, shouldLoad = true }) => {
  const { selectedTenantId } = useContext(AuthContext);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [lifecycleStages, setLifecycleStages] = useState<LifecycleStage[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  const fetchData = async () => {
    if (loading || !selectedTenantId || !isMainApiCompleted || !shouldLoad) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch all reference data in parallel
      const [contactsData, stagesData, leadsData, pipelinesData] = await Promise.all([
        ContactService.getAllContacts(),
        DealService.getDealStages(parseInt(selectedTenantId)),
        LeadService.getAllLeads(),
        PipelineService.getPipelines()
      ]);

      setContacts(contactsData);
      setLifecycleStages(stagesData || []);
      setLeads(leadsData);
      setPipelines(pipelinesData);
      setDataLoaded(true);
    } catch (err: any) {
      console.error('Error fetching deal form data:', err);
      setError(err.message || 'Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setDataLoaded(false);
    await fetchData();
  };

  const getFilteredContacts = (companyId: number | null): Contact[] => {
    if (!companyId) return [];
    return contacts.filter(contact => contact.company_id === companyId);
  };

  // Fetch data when tenant changes or on first load - only run when main API has completed and the data is actually needed
  useEffect(() => {
    if (selectedTenantId && isMainApiCompleted && shouldLoad && !dataLoaded && !loading) {
      fetchData();
    }
  }, [selectedTenantId, isMainApiCompleted, shouldLoad, dataLoaded, loading]);

  const value: DealFormData = {
    contacts,
    lifecycleStages,
    leads,
    pipelines,
    loading,
    error,
    refreshData,
    getFilteredContacts
  };

  return (
    <DealFormDataContext.Provider value={value}>
      {children}
    </DealFormDataContext.Provider>
  );
};

export const useDealFormData = (): DealFormData => {
  const context = useContext(DealFormDataContext);
  if (context === undefined) {
    throw new Error('useDealFormData must be used within a DealFormDataProvider');
  }
  return context;
};
